import React, { useState } from "react";
import { Button, Select, Input, Alert, Progress, Spin, Switch } from "antd";
import { DownloadOutlined, CheckCircleOutlined, CloudUploadOutlined } from "@ant-design/icons";
import StatStrip from "../../../components/dashboard/StatStrip";
import { NAVY, BLUE, GREEN, RED } from "../gateway.constants";

const { Option } = Select;

function GeneratePanel({
  fields, mappings, sftpDests, generating, progress,
  jobStatus, jobResult, preflightReport,
  onGenerate, onDownload, onBack, onReset,
}) {
  const [sftpId,      setSftpId]      = useState(null);
  const [saveMapping, setSaveMapping] = useState(false);
  const [saveName,    setSaveName]    = useState("");

  const completed = jobStatus === "COMPLETED";
  const failed    = jobStatus === "FAILED";
  const mc = mappings.filter((m) => m.sourceColumn).length;
  const mt = fields.filter((f) => f.isMandatory && !f.defaultValue).length;
  const mm = fields
    .filter((f) => f.isMandatory && !f.defaultValue)
    .filter((f) => mappings.find((m) => m.targetField === f.fieldName && m.sourceColumn)).length;

  const stats = [
    { label: "Fields mapped",   value: `${mc}/${fields.length}`,   color: "#93C5FD" },
    { label: "Mandatory",       value: `${mm}/${mt}`,              color: mm === mt ? "#86EFAC" : "#FCA5A5" },
    { label: "Pre-flight err",  value: preflightReport?.errorCount ?? "—",   color: (preflightReport?.errorCount || 0) === 0 ? "#86EFAC" : "#FCA5A5" },
    { label: "Warnings",        value: preflightReport?.warningCount ?? "—", color: "#FCD34D" },
  ];

  return (
    <div>
      <StatStrip stats={stats} style={{ marginBottom: 20, borderRadius: 10 }} />

      {/* SFTP */}
      {sftpDests.length > 0 && !completed && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
            <CloudUploadOutlined style={{ marginRight: 6 }} />SFTP delivery (optional)
          </div>
          <Select allowClear placeholder="Select SFTP destination"
            style={{ width: "100%" }} value={sftpId} onChange={setSftpId}>
            {sftpDests.map((s) => (
              <Option key={s.sftpId} value={s.sftpId}>
                {s.displayName} — {s.host}:{s.port}{s.remotePath}
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* Save mapping toggle */}
      {!completed && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <Switch size="small" checked={saveMapping} onChange={setSaveMapping} />
          <span style={{ fontSize: 13 }}>Save mapping for reuse</span>
          {saveMapping && (
            <Input size="small" style={{ width: 200 }} placeholder="Mapping name"
              value={saveName} onChange={(e) => setSaveName(e.target.value)} />
          )}
        </div>
      )}

      {/* Progress */}
      {generating && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Spin size="small" />
            <span>Generating… {jobStatus && <span style={{ color: "#888" }}>({jobStatus})</span>}</span>
          </div>
          <Progress
            percent={progress}
            status={failed ? "exception" : progress === 100 ? "success" : "active"}
            strokeColor={{ from: BLUE, to: GREEN }}
          />
          {jobResult && (
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 6 }}>
              {jobResult.processedRows || 0} / {jobResult.totalRows || "?"} rows processed
            </div>
          )}
        </div>
      )}

      {/* Success */}
      {completed && jobResult && (
        <Alert type="success" showIcon style={{ marginBottom: 16 }}
          message={`Generated — ${jobResult.totalRows} rows`}
          description={
            <div>
              <div>File: <strong>{jobResult.outputFilename}</strong></div>
              {jobResult.outputFileHash && (
                <div style={{ fontFamily: "monospace", fontSize: 10, color: "#666", marginTop: 2 }}>
                  SHA-256: {jobResult.outputFileHash.slice(0, 24)}…
                </div>
              )}
              {jobResult.sftpDelivered && (
                <div style={{ color: GREEN, marginTop: 4 }}>
                  <CheckCircleOutlined /> Delivered to SFTP
                </div>
              )}
            </div>
          }
        />
      )}

      {/* Failure */}
      {failed && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="Generation failed"
          description={jobResult?.errorMessage || "Unknown error"} />
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {!generating && !completed && <Button onClick={onBack}>Back</Button>}
        {!completed && !generating && (
          <Button type="primary" icon={<DownloadOutlined />}
            style={{ background: NAVY, borderColor: NAVY }}
            onClick={() => onGenerate(sftpId, saveMapping ? saveName : null)}>
            Generate file
          </Button>
        )}
        {completed && (
          <Button type="primary" icon={<DownloadOutlined />}
            style={{ background: GREEN, borderColor: GREEN }}
            onClick={onDownload}>
            Download file
          </Button>
        )}
        {(completed || failed) && <Button onClick={onReset}>Start over</Button>}
      </div>
    </div>
  );
}

export default GeneratePanel;
